import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  TrendingUp, 
  TrendingDown, 
  Eye, 
  Users, 
  Activity, 
  Share2,
  Calendar,
  BarChart3,
  PieChart,
  Download,
  Filter,
  RefreshCw,
  MessageSquare,
  Star as StarIcon,
  MousePointerClick,
  Clock,
  AlertCircle
} from 'lucide-react';
import { useAnalytics } from '../../../contexts/AnalyticsContext';
import { analyticsAPI } from '../../../services/api';
import toast from 'react-hot-toast';

const Analytics = () => {
  const { trackEvent } = useAnalytics();
  const [timeRange, setTimeRange] = useState('30d');
  const [isLoading, setIsLoading] = useState(true);
  const [hasProfileError, setHasProfileError] = useState(false);
  
  const [analytics, setAnalytics] = useState({
    overview: {
      totalViews: 0,
      totalClicks: 0,
      totalEngagements: 0,
      uniqueVisitors: 0,
      engagementRate: 0,
      averageTime: '0m 0s',
      bounceRate: 0,
    },
    visits: [],
    clicks: [],
    devices: [],
    time: [],
    referrers: [],
    elementRankings: []
  });
  
  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackStats, setFeedbackStats] = useState({ avgRating: 0, totalCount: 0, breakdown: {} });

  useEffect(() => {
    fetchData();
  }, [timeRange]);

  const fetchData = async () => {
    setIsLoading(true);
    setHasProfileError(false);
    
    try {
        // Fetch Analytics
        const resAnalytics = await analyticsAPI.getUserAnalytics({ period: timeRange });
        if (resAnalytics.data.success) {
            setAnalytics(resAnalytics.data.data);
        }
    } catch (error) {
        console.error('Failed to load analytics', error);
        if (error.response?.status === 404) {
            setHasProfileError(true);
        } else {
            toast.error('Failed to fetch analytics data.');
        }
    }

    try {
        // Fetch Internal Feedback
        const resFeedback = await analyticsAPI.getInternalFeedback();
        if (resFeedback.data.success) {
            setFeedbacks(resFeedback.data.data.feedbacks || []);
            setFeedbackStats(resFeedback.data.data.stats || { avgRating: 0, totalCount: 0, breakdown: {} });
        }
    } catch (error) {
        console.error('Failed to load internal feedback', error);
        // We do not show an error toast here to avoid spamming the user, especially if they just don't have a profile yet.
    }
    
    setIsLoading(false);
  };

  const StatCard = ({ title, value, change, icon: Icon, color, subvalue }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
    >
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{title}</p>
          <div className="flex items-baseline space-x-2">
            <p className="text-2xl font-bold text-gray-900 dark:text-white mt-1">{value}</p>
            {subvalue && <span className="text-sm text-gray-500">{subvalue}</span>}
          </div>
          {change !== undefined && change !== null && (
            <div className="flex items-center mt-2">
              {change > 0 ? (
                <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
              ) : change < 0 ? (
                <TrendingDown className="w-4 h-4 text-red-500 mr-1" />
              ) : (
                <div className="w-4 h-4 mr-1 text-gray-400">-</div>
              )}
              <span className={`text-sm ${change > 0 ? 'text-green-600 dark:text-green-400' : change < 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>
                {change !== 0 ? `${Math.abs(change)}% from last period` : 'No change'}
              </span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg ${color}`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
      </div>
    </motion.div>
  );

  const ChartCard = ({ title, children, className = "" }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 ${className}`}
    >
      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{title}</h3>
      {children}
    </motion.div>
  );

  // Dynamic Pie Chart Helper
  const PieChartComponent = ({ data, size = 120 }) => {
    // data shape: { name, value, color, emoji }
    // assign colors if not present
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];
    const processedData = data.map((d, i) => ({ ...d, color: d.color || colors[i % colors.length] }));
    const total = processedData.reduce((sum, item) => sum + item.value, 0);
    
    if (total === 0) {
      return (
        <div className="flex items-center justify-center" style={{ width: size, height: size }}>
          <div className="rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-xs text-gray-400" style={{ width: size, height: size }}>
            No Data
          </div>
        </div>
      );
    }

    let currentAngle = 0;

    return (
      <div className="flex items-center justify-center relative">
        <svg width={size} height={size} className="transform -rotate-90 drop-shadow-sm">
          {processedData.map((item, index) => {
            const percentage = (item.value / total) * 100;
            const angle = (percentage / 100) * 360;
            const startAngle = currentAngle;
            currentAngle += angle;

            // Math to draw arcs
            const x1 = size / 2 + (size / 2 - 10) * Math.cos((startAngle * Math.PI) / 180);
            const y1 = size / 2 + (size / 2 - 10) * Math.sin((startAngle * Math.PI) / 180);
            const x2 = size / 2 + (size / 2 - 10) * Math.cos((currentAngle * Math.PI) / 180);
            const y2 = size / 2 + (size / 2 - 10) * Math.sin((currentAngle * Math.PI) / 180);

            const largeArcFlag = angle > 180 ? 1 : 0;

            // Only draw a full circle if it's 100% to avoid SVG path bug
            if (angle === 360) {
                return <circle key={index} cx={size/2} cy={size/2} r={size/2 - 10} fill={item.color} />;
            }

            return (
              <path
                key={index}
                d={`M ${size / 2} ${size / 2} L ${x1} ${y1} A ${size / 2 - 10} ${size / 2 - 10} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`}
                fill={item.color}
                className="transition-all duration-300 hover:opacity-80 stroke-white dark:stroke-gray-800"
                strokeWidth="2"
              />
            );
          })}
        </svg>
      </div>
    );
  };

  // Dynamic Bar Chart Helper (Views only since clicks aren"t perfectly day matched yet)
  const BarChartComponent = ({ data, height = 200 }) => {
    if (!data || data.length === 0) {
        return <div className="h-48 flex items-center justify-center text-gray-500">No views in this period.</div>;
    }
    
    const maxViews = Math.max(...data.map(item => item.views), 1); // Avoid div by 0
    
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
          <div className="flex items-center space-x-4">
            <div className="flex items-center">
              <div className="w-3 h-3 bg-blue-500 rounded mr-2"></div>
              <span>Profile Views</span>
            </div>
          </div>
        </div>
        <div className="flex items-end space-x-2 h-48 overflow-x-auto pb-2 custom-scrollbar">
          {data.map((item, index) => (
            <div key={index} className="flex-1 min-w-[30px] flex flex-col items-center space-y-2 group relative">
              {/* Tooltip */}
              <div className="absolute -top-8 bg-gray-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 whitespace-nowrap z-10 transition-opacity pointer-events-none">
                {item.views} Views
              </div>
              
              <div className="flex flex-col justify-end h-full w-full max-w-[40px]">
                <div 
                  className="bg-blue-500 rounded-t-sm w-full transition-all duration-500"
                  style={{ height: `${(item.views / maxViews) * 100}%`, minHeight: item.views > 0 ? '4px' : '0' }}
                ></div>
              </div>
              <span className="text-[10px] text-gray-500 dark:text-gray-400 whitespace-nowrap rotate-[-45deg] origin-top-left -ml-2 mt-2">
                {new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Helper to colorize Pie elements 
  const getColors = () => ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6'];

  if (hasProfileError) {
      return (
          <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
              <div className="w-20 h-20 bg-orange-100 dark:bg-orange-900/30 rounded-full flex items-center justify-center mb-6">
                  <AlertCircle className="w-10 h-10 text-orange-500" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">Setup Your Profile First</h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mb-6">
                  Analytics will start tracking as soon as you create and publish your public digital profile.
              </p>
              <button onClick={() => window.location.href='/app/dashboard/profile'} className="px-6 py-2.5 bg-primary-500 text-white font-bold rounded-lg hover:bg-primary-600 transition-colors">
                  Go to Profile Setup
              </button>
          </div>
      )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4"
      >
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Analytics</h1>
          <p className="text-gray-600 dark:text-gray-400">Track and grow your audience engagement</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={fetchData}
            disabled={isLoading}
            className="p-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          </motion.button>
        </div>
      </motion.div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Profile Visits"
          value={analytics.overview.totalViews.toLocaleString()}
          icon={Eye}
          color="bg-blue-100 dark:bg-blue-900"
        />
        <StatCard
          title="Link Clicks (WhatsApp, Google)"
          value={analytics.overview.totalClicks.toLocaleString()}
          icon={MousePointerClick}
          color="bg-green-100 dark:bg-green-900"
        />
        <StatCard
          title="Engagement Rate"
          value={`${analytics.overview.engagementRate}%`}
          icon={TrendingUp}
          color="bg-purple-100 dark:bg-purple-900"
        />
        <StatCard
          title="Avg. Time on Page"
          value={analytics.overview.averageTime}
          icon={Clock}
          color="bg-orange-100 dark:bg-orange-900"
        />
      </div>

       {/* Internal Feedback Section (MOVED UP FOR VISIBILITY) */}
       <div className="w-full relative">
         <div className="absolute -inset-1 bg-gradient-to-r from-orange-500 to-rose-500 rounded-2xl blur opacity-20 dark:opacity-30 pointer-events-none"></div>
         <ChartCard title={
            <div className="flex items-center gap-2">
                <StarIcon className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                <span className="text-xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-orange-600 to-rose-500 dark:from-orange-400 dark:to-rose-400">
                    Private Customer Feedback (≤ 3 Stars)
                </span>
            </div>
         } className="relative z-10 border-orange-100 dark:border-rose-900/40 bg-gradient-to-br from-white to-orange-50 dark:from-gray-800 dark:to-gray-800 shadow-lg">
             
             {/* Stats Row */}
             <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                 <div className="bg-white dark:bg-gray-700/50 p-5 rounded-2xl shadow-sm border border-orange-100 dark:border-gray-600 flex flex-col justify-center items-center text-center">
                     <p className="text-sm font-semibold text-gray-500 dark:text-gray-400 mb-1">Average Feedback Rating</p>
                     <div className="flex items-center gap-2">
                         <span className="text-4xl font-extrabold text-gray-900 dark:text-white">{feedbackStats.avgRating}</span>
                         <span className="text-xl font-medium text-gray-400">/ 3</span>
                     </div>
                     <p className="text-xs text-orange-600 dark:text-orange-400 mt-2 font-medium bg-orange-100 dark:bg-orange-900/40 px-3 py-1 rounded-full">
                         From {feedbackStats.totalCount} internal reviews
                     </p>
                 </div>
                 
                 <div className="md:col-span-2 bg-white dark:bg-gray-700/50 p-5 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-600">
                      <h4 className="text-gray-800 dark:text-gray-200 font-semibold mb-4 text-sm uppercase tracking-wider">
                           Main Issues Reported
                      </h4>
                      <div className="flex flex-wrap gap-2.5">
                          {Object.entries(feedbackStats.breakdown || {}).map(([category, count]) => (
                              <div key={category} className="bg-gray-50 dark:bg-gray-800 px-4 py-2 rounded-xl text-sm border border-gray-200 dark:border-gray-600 shadow-sm flex items-center gap-3">
                                  <span className="text-gray-800 dark:text-gray-200 font-medium">{category}</span>
                                  <span className="bg-rose-100 text-rose-600 dark:bg-rose-900/50 dark:text-rose-400 font-bold text-xs px-2.5 py-1 rounded-md">
                                      {count} issues
                                  </span>
                              </div>
                          ))}
                          {Object.keys(feedbackStats.breakdown || {}).length === 0 && (
                              <div className="w-full flex items-center text-green-600 dark:text-green-400 text-sm font-medium p-3 bg-green-50 dark:bg-green-900/20 rounded-xl border border-green-100 dark:border-green-800/30">
                                  🎉 No negative feedback reported yet! Keep up the great work!
                              </div>
                          )}
                      </div>
                 </div>
             </div>
    
             {/* Feedbacks List */}
             <div>
                <h4 className="text-gray-800 dark:text-gray-200 font-semibold mb-3 text-sm uppercase tracking-wider">Detailed Feedback</h4>
                <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                    {feedbacks.length > 0 ? (
                        feedbacks.map((item) => (
                            <div key={item._id} className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600/60 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-700/50 px-3 py-1.5 rounded-lg border border-gray-100 dark:border-gray-600">
                                        {[1, 2, 3].map(star => (
                                            <StarIcon key={star} className={`w-4 h-4 ${item.rating >= star ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300 dark:text-gray-600'}`} />
                                        ))}
                                        <div className="w-px h-4 bg-gray-300 dark:bg-gray-600 mx-1"></div>
                                        <span className="text-sm font-bold text-gray-800 dark:text-gray-200">{item.category}</span>
                                    </div>
                                    <span className="text-xs font-medium text-gray-500 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded">
                                        {new Date(item.createdAt).toLocaleDateString()}
                                    </span>
                                </div>
                                <div className="pl-1">
                                    <p className="text-gray-600 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap italic">
                                        "{item.comment || "No additional commentary provided."}"
                                    </p>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="text-center py-10 bg-white dark:bg-gray-800/50 rounded-2xl border border-dashed border-gray-200 dark:border-gray-600">
                            <div className="inline-flex items-center justify-center w-14 h-14 bg-gray-50 dark:bg-gray-700 rounded-full mb-3 text-2xl">
                                🛡️
                            </div>
                            <h3 className="text-gray-900 dark:text-white font-semibold mb-1">Your reputation is spotless.</h3>
                            <p className="text-gray-500 dark:text-gray-400 text-sm max-w-sm mx-auto">
                                All negative/low star reviews will be securely captured here instead of landing on your Google Profile.
                            </p>
                        </div>
                    )}
                </div>
             </div>
         </ChartCard>
       </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Trends Chart */}
        <ChartCard title="Profile Visits Over Time" className="lg:col-span-2">
          {isLoading ? (
              <div className="h-48 flex items-center justify-center animate-pulse bg-gray-100 dark:bg-gray-800 rounded-xl"></div>
          ) : (
              <BarChartComponent data={analytics.visits} />
          )}
        </ChartCard>

        {/* Traffic Sources explicitly tracking Clicks as Ranked Graph */}
        <ChartCard title="Click Engagement by Platform (Ranked)">
          {isLoading ? (
             <div className="h-40 flex items-center justify-center animate-pulse bg-gray-100 dark:bg-gray-800 rounded-xl"></div> 
          ) : (
            <div className="w-full space-y-4 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                {analytics.elementRankings && analytics.elementRankings.length > 0 ? analytics.elementRankings.map((source, index) => {
                    // elementRankings comes sorted by clicks from backend
                    const maxClicks = Math.max(...analytics.elementRankings.map(s => s.clicks), 1);
                    const percentageWidth = Math.max((source.clicks / maxClicks) * 100, 2); // At least 2% width for visibility
                    
                    return (
                        <div key={index} className="flex relative items-center w-full bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-100 dark:border-gray-700 overflow-hidden group">
                           {/* Progress Bar Background */}
                           <div 
                              className="absolute left-0 top-0 bottom-0 opacity-10 dark:opacity-20 transition-all duration-1000 ease-out"
                              style={{ 
                                  width: `${percentageWidth}%`,
                                  backgroundColor: getColors()[index % getColors().length] 
                              }}
                           ></div>
                           
                           {/* Rank Badge */}
                           <div className="flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center bg-white dark:bg-gray-700 shadow-sm mr-4 z-10 font-bold text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-600">
                               #{index + 1}
                           </div>

                           {/* Content */}
                           <div className="flex-grow flex flex-col sm:flex-row sm:items-center justify-between z-10 gap-2">
                               <div className="flex items-center gap-3">
                                   <span className="text-2xl leading-none">{source.emoji || '🔗'}</span>
                                   <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 capitalize">
                                       {source.elementName.replace('_', ' ')}
                                   </span>
                               </div>
                               
                               <div className="flex items-center gap-3 justify-end">
                                   <div className="text-right">
                                        <span className="block text-lg font-bold text-gray-900 dark:text-white leading-none">{source.clicks}</span>
                                        <span className="text-[10px] text-gray-500 uppercase tracking-wider font-semibold">Clicks</span>
                                   </div>
                               </div>
                           </div>
                        </div>
                    )
                }) : (
                    <div className="text-sm text-gray-500 text-center w-full py-8">No click data available yet.</div>
                )}
            </div>
          )}
        </ChartCard>

        {/* Device Distribution */}
        <ChartCard title="Device Distribution">
          {isLoading ? (
             <div className="h-40 flex items-center justify-center animate-pulse bg-gray-100 dark:bg-gray-800 rounded-xl"></div> 
          ) : (
            <div className="flex flex-col sm:flex-row items-center space-y-6 sm:space-y-0 sm:space-x-8">
                <PieChartComponent 
                    data={analytics.devices.map((d, i) => ({ 
                        name: d.name, 
                        value: d.value, // value is already percentage from backend
                        color: getColors()[i % getColors().length] 
                    }))} 
                />
                <div className="flex-1 w-full space-y-3">
                {analytics.devices.length > 0 ? analytics.devices.map((device, index) => (
                    <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div 
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: getColors()[index % getColors().length] }}
                        ></div>
                        <span className="text-xl leading-none">{device.emoji || '📱'}</span>
                        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{device.name}</span>
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{device.value}%</span>
                    </div>
                )) : (
                    <div className="text-sm text-gray-500 text-center w-full">No device data available yet.</div>
                )}
                </div>
            </div>
          )}
        </ChartCard>
      </div>

    </div>
  );
};

export default Analytics;